import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Inbox, RefreshCw, LogOut, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0 });
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("verra_token");
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const logout = useCallback(() => {
    localStorage.removeItem("verra_token");
    navigate("/login");
  }, [navigate]);

  const openSession = useCallback(async (sid) => {
    setActive(sid);
    try {
      const { data } = await axios.get(`${API}/chat/${sid}`);
      setTranscript(data.messages || []);
    } catch (e) { setTranscript([]); }
  }, []);

  const load = useCallback(async () => {
    if (!token) { navigate("/login"); return; }
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        axios.get(`${API}/inquiries`, authHeader),
        axios.get(`${API}/inquiries/stats`, authHeader),
        axios.get(`${API}/chat/sessions/list`, authHeader),
      ]);
      setItems(a.data);
      setStats(b.data);
      setSessions(c.data);
    } catch (e) {
      if (e.response?.status === 401) { logout(); return; }
    }
    setLoading(false);
  }, [token, navigate, logout]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-verra-base p-6 md:p-12" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-4xl text-verra-blue">Inquiries</h1>
            <p className="text-verra-blue/60 mt-1">Verraleaf lead inbox</p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={load} className="inline-flex items-center gap-2 rounded-full bg-verra-blue text-white px-5 py-2.5 text-sm font-semibold hover:bg-verra-green transition-colors">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-verra-blue/20 text-verra-blue px-5 py-2.5 text-sm font-semibold hover:bg-verra-grey transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="rounded-2xl bg-white border border-verra-blue/10 p-6">
            <p className="text-verra-blue/60 text-sm">Total Inquiries</p>
            <p className="font-display text-4xl text-verra-blue mt-1" data-testid="stat-total">{stats.total}</p>
          </div>
          <div className="rounded-2xl bg-white border border-verra-blue/10 p-6">
            <p className="text-verra-blue/60 text-sm">New</p>
            <p className="font-display text-4xl text-verra-green mt-1" data-testid="stat-new">{stats.new}</p>
          </div>
        </div>

        {loading ? (
          <p className="text-verra-blue/50">Loading…</p>
        ) : items.length === 0 ? (
          <div className="rounded-2xl bg-white border border-verra-blue/10 p-16 text-center text-verra-blue/50">
            <Inbox className="w-10 h-10 mx-auto mb-3" /> No inquiries yet.
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((it) => (
              <div key={it.id} data-testid="inquiry-row" className="rounded-2xl bg-white border border-verra-blue/10 p-6">
                <div className="flex flex-wrap justify-between gap-2">
                  <p className="font-semibold text-verra-blue">{it.name} {it.company && <span className="text-verra-blue/50">· {it.company}</span>}</p>
                  <span className="text-sm text-verra-blue/50">{new Date(it.created_at).toLocaleString()}</span>
                </div>
                <div className="text-sm text-verra-blue/70 mt-2 flex flex-wrap gap-x-6 gap-y-1">
                  <span>{it.email}</span>
                  {it.phone && <span>{it.phone}</span>}
                  {it.country && <span>{it.country}</span>}
                  {it.product_interest && <span className="text-verra-green font-medium">{it.product_interest}</span>}
                  {it.quantity && <span>Qty: {it.quantity}</span>}
                </div>
                {it.message && <p className="text-verra-blue/60 text-sm mt-3">{it.message}</p>}
              </div>
            ))}
          </div>
        )}

        {/* Assistant Chats */}
        <div className="mt-14">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-5 h-5 text-verra-green" />
            <h2 className="font-display text-2xl text-verra-blue">Assistant Chats</h2>
            <span className="text-verra-blue/50 text-sm">({sessions.length})</span>
          </div>
          {sessions.length === 0 ? (
            <p className="text-verra-blue/50 text-sm">No assistant conversations yet.</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2" data-testid="chat-session-list">
                {sessions.map((s) => (
                  <button
                    key={s.session_id}
                    data-testid="chat-session"
                    onClick={() => openSession(s.session_id)}
                    className={`w-full text-left rounded-xl border p-4 transition-colors ${active === s.session_id ? "border-verra-green bg-verra-light/10" : "border-verra-blue/10 bg-white hover:border-verra-green/40"}`}
                  >
                    <div className="flex justify-between gap-2">
                      <span className="text-sm font-medium text-verra-blue">{s.messages} messages</span>
                      <span className="text-xs text-verra-blue/40">{s.last_at ? new Date(s.last_at).toLocaleString() : ""}</span>
                    </div>
                    <p className="text-sm text-verra-blue/60 mt-1 truncate">{s.last_message}</p>
                  </button>
                ))}
              </div>
              <div className="rounded-2xl border border-verra-blue/10 bg-white p-4 h-[420px] overflow-y-auto" data-testid="chat-transcript">
                {active ? (
                  transcript.map((m, i) => (
                    <div key={i} className={`flex mb-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "bg-verra-blue text-white" : "bg-verra-grey text-verra-blue"}`}>
                        {m.content}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-verra-blue/40 text-sm">Select a conversation to view the transcript.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
